import { useEffect, useState } from 'react';
import { Badge, Button, Panel, PanelBody, PanelHeader, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import type { NotificationTemplatePreview } from '@/lib/types';
import type { CatalogueEventEntry } from './log-labels';

interface Props {
  entry: CatalogueEventEntry;
  /** The caller's own address, named in the test result. */
  selfEmail: string | null;
}

type TestResult = { tone: 'success' | 'danger'; message: string } | null;

/**
 * Subject + a sandboxed preview of one event's branded email.
 *
 * ⚠️ The preview is an `<iframe sandbox="" srcDoc>` — the empty token list, as
 * `OnboardingEmailModal` does. Never `dangerouslySetInnerHTML`, never a token.
 */
export function TemplatePreviewPanel({ entry, selfEmail }: Props) {
  const eventKey = entry.event.key;
  const [preview, setPreview] = useState<NotificationTemplatePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState<TestResult>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    // Fresh state per event: the parent keys this panel on the event key.
    api
      .previewNotificationTemplate(eventKey, signal)
      .then((res) => {
        if (!signal.aborted) setPreview(res.data);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setError(caught instanceof Error ? caught.message : 'Failed to load the preview');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [eventKey]);

  async function sendTest() {
    setSending(true);
    setTestResult(null);
    try {
      await api.sendNotificationTest(eventKey);
      setTestResult({
        tone: 'success',
        message: selfEmail ? `Test email queued for ${selfEmail}.` : 'Test email queued.',
      });
    } catch (caught) {
      setTestResult({
        tone: 'danger',
        message:
          caught instanceof ApiError
            ? caught.message
            : 'Could not reach the server. No test email was sent.',
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <Panel className="min-w-0" aria-labelledby="template-preview-heading">
      <PanelHeader
        id="template-preview-heading"
        title={entry.event.label}
        description={`${entry.moduleLabel} · ${entry.submoduleLabel}`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={sending}
            disabled={!preview}
            onClick={() => void sendTest()}
          >
            Send test to me
          </Button>
        }
      />
      <PanelBody className="grid min-w-0 gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {entry.event.locked && <Badge tone="warning">Always on</Badge>}
          <code className="text-xs text-fg-muted [overflow-wrap:anywhere]">{eventKey}</code>
        </div>

        {testResult && (
          <Notice tone={testResult.tone} role={testResult.tone === 'danger' ? 'alert' : 'status'}>
            <span className="[overflow-wrap:anywhere]">{testResult.message}</span>
          </Notice>
        )}

        {error ? (
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load the preview.</>}>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        ) : loading || !preview ? (
          <div className="grid gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-96 w-full" />
          </div>
        ) : (
          <>
            <dl className="m-0 grid gap-0.5">
              <dt className="text-xs font-medium text-fg-secondary">Subject</dt>
              <dd className="m-0 text-sm text-fg [overflow-wrap:anywhere]">{preview.subject}</dd>
            </dl>
            <iframe
              title={`Preview of the ${entry.event.label} email`}
              sandbox=""
              srcDoc={preview.html}
              className="h-[36rem] w-full rounded-lg border border-border bg-white"
            />
          </>
        )}
      </PanelBody>
    </Panel>
  );
}
